import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ListBox } from 'primereact/listbox';
import DateInput from './DateInput';
import { ACTIVITY_TYPES, activityLabel, newDetail, normalizeDetail, nextStartTime, sortDetails, uOdmor } from '../../utils/activities';
import { fmtDuration, fmtTime, DAY_MS } from '../../utils/dates';
import { useAlerts } from '../../custom/Alerts';
import { tableEmpty } from './PageHeader';
import AktivnostSimbol from './AktivnostSimbol';

/* Inline-editabilna tabela detalja aktivnosti jednog dana (Tip / Početak / Kraj / Trajanje).
   Isto ponašanje kao stari DataGrid: "+" dodaje red na vrhu, olovka menja, X briše,
   posle svake izmene lista se normalizuje i roditelj dobija `onChange(list)`.
   `visestruko`: kvadratići za izbor redova + "Obriši aktivnosti" — izabrani redovi postaju odmor (uOdmor). */
export default function ActivityDetailsGrid({
    details, day, onChange, confirmDelete = false, readOnly = false, selectable = false, visestruko = false,
    emptyMessage = 'Nijedna aktivnost nije pronađena', startWidth, scrollHeight = 'flex', extraColumns = [], simboli = true, prednostIzmeni = false,
}) {
    const alerts = useAlerts();
    const [edit, setEdit] = useState(null); // { key, isNew, row }
    const [tipRed, setTipRed] = useState(null); // red kome je dvoklikom otvoren izbor tipa
    const [izabrani, setIzabrani] = useState([]);
    const tipPanel = useRef(null);
    const dan = day ? new Date(day).getTime() : null;
    useEffect(() => { setIzabrani([]); }, [dan]); // drugi dan = izbor ispočetka

    const keyOf = (d) => d._key || d.id;
    const withKeys = details.map((d, i) => (d._key ? d : { ...d, _key: `e${d.id}-${i}` }));
    const rows = edit && edit.isNew ? [edit.row, ...withKeys] : withKeys;

    const startNew = () => {
        const start = nextStartTime(details, day);
        const end = new Date(start);
        setEdit({ key: '__new', isNew: true, row: { ...newDetail('driving', start, end), _key: '__new' } });
    };

    const commit = () => {
        /* kao stari grid: samo normalizacija; ispravnost dana proverava zbir od 24:00 */
        const row = normalizeDetail(edit.row);
        const base = edit.isNew ? withKeys : withKeys.filter((d) => keyOf(d) !== edit.key);
        const final = edit.isNew ? { ...row, _key: undefined } : row;
        /* prednostIzmeni: dodata/izmenjena aktivnost ima prednost — ostali redovi se oko nje skraćuju ili dele
           (Bulk / Taho listići: ranije je nova aktivnost preko postojeće nestajala) */
        const ostali = prednostIzmeni ? base.flatMap((d) => {
            const a = new Date(d.startTime).getTime(), b = new Date(d.endTime).getTime();
            const fa = new Date(final.startTime).getTime(), fb = new Date(final.endTime).getTime();
            if (fb <= fa || b <= fa || a >= fb) return [d];
            const delovi = [];
            if (a < fa) delovi.push(normalizeDetail({ ...d, endTime: new Date(fa) }));
            if (b > fb) delovi.push(normalizeDetail({ ...d, startTime: new Date(fb), _key: a < fa ? `${keyOf(d)}-2` : d._key }));
            return delovi;
        }) : base;
        onChange(sortDetails([...ostali, final]));
        setEdit(null);
    };

    const remove = async (row) => {
        if (confirmDelete && !(await alerts.confirm('Potvrdi', 'Da li ste sigurni da želite da obrišete aktivnost?'))) return;
        onChange(withKeys.filter((d) => keyOf(d) !== keyOf(row)));
    };

    /* više redova odjednom: umesto obrisanih ostaje odmor (dan ostaje pun) */
    const obrisiIzabrane = async () => {
        const n = izabrani.length;
        if (!n) return;
        const tekst = `Obrisati ${n === 1 ? 'izabranu aktivnost' : `${n} izabranih aktivnosti`}? Na njihovom mestu biće odmor.`;
        if (!(await alerts.confirm('Potvrdi', tekst, { severity: 'danger' }))) return;
        onChange(uOdmor(withKeys, new Set(izabrani.map(keyOf)), keyOf));
        setIzabrani([]);
    };

    const setField = (field, value) => setEdit((e) => ({ ...e, row: { ...e.row, [field]: value } }));

    /* time-only editor: datumski deo ostaje referentni dan aktivnosti. */
    const timeValue = (field) => {
        const v = edit.row[field];
        return v ? new Date(v) : null;
    };
    const setTime = (field, v) => {
        if (!v) return setField(field, null);
        const base = new Date(day);
        base.setHours(v.getHours(), v.getMinutes(), 0, 0);
        setField(field, base);
    };

    const isEditing = (row) => edit && keyOf(row) === edit.key;

    /* dvoklik na red: lista tipova ispod reda, izbor odmah upisuje (brza ispravka pogrešnog tipa).
       Jedan panel van tabele — meni unutar ćelije DataTable ponovo iscrtava i zatvara. */
    const brziTip = (e) => {
        if (readOnly || edit) return;
        const tr = e.originalEvent.target.closest('tr');
        setTipRed(e.data);
        tipPanel.current?.show(e.originalEvent, tr || e.originalEvent.target);
    };
    const upisiTip = (tip) => {
        const row = tipRed;
        tipPanel.current?.hide();
        if (row && tip && tip !== row.activityType) {
            onChange(sortDetails(withKeys.map((d) => (keyOf(d) === keyOf(row) ? normalizeDetail({ ...d, activityType: tip }) : d))));
        }
    };

    const typeBody = (row) =>
        isEditing(row) ? (
            <Dropdown value={edit.row.activityType} options={ACTIVITY_TYPES} onChange={(e) => setField('activityType', e.value)} style={{ width: '100%' }} />
        ) : simboli ? <AktivnostSimbol tip={row.activityType} label={activityLabel(row.activityType)} /> : activityLabel(row.activityType);

    const timeBody = (field) => (row) =>
        isEditing(row) ? (
            <DateInput value={timeValue(field)} onChange={(e) => setTime(field, e.value)} timeOnly hourFormat="24" placeholder="Unesite datum/vreme" />
        ) : fmtTime(row[field]);

    const totalBody = (row) => {
        const ms = isEditing(row) ? normalizeDetail(edit.row).totalTime : row.totalTime;
        return ms === DAY_MS ? '24:00' : fmtDuration(ms);
    };

    const actionsBody = (row) =>
        isEditing(row) ? (
            <span className="p-buttonset">
                <Button type="button" icon="pi pi-check" rounded text severity="success" onClick={commit} tooltip="Potvrdi" aria-label="Potvrdi izmenu" />
                <Button type="button" icon="pi pi-times" rounded text severity="secondary" onClick={() => setEdit(null)} tooltip="Otkaži" aria-label="Otkaži izmenu" />
            </span>
        ) : (
            <span>
                <Button type="button" icon="pi pi-pencil" rounded text severity="secondary" onClick={() => setEdit({ key: keyOf(row), isNew: false, row: { ...row } })}
                    disabled={!!edit} aria-label="Izmeni aktivnost" tooltip="Izmeni" />
                <Button type="button" icon="pi pi-trash" rounded text severity="danger" onClick={() => remove(row)} disabled={!!edit} aria-label="Obriši aktivnost" tooltip="Obriši" />
            </span>
        );

    const addHeader = (
        <Button type="button" icon="pi pi-plus" rounded text onClick={startNew} disabled={!!edit} tooltip="Dodaj aktivnost" aria-label="Dodaj aktivnost" />
    );

    /* kao stari grid: Enter potvrđuje, Esc odustaje od izmene reda (osim dok je otvoren padajući meni) */
    const onKeyDown = (e) => {
        if (!edit || (e.key !== 'Enter' && e.key !== 'Escape')) return;
        if (document.querySelector('.p-dropdown-panel')) return;
        e.preventDefault();
        if (e.key === 'Enter') commit(); else setEdit(null);
    };

    const vise = visestruko && !readOnly;
    /* izbor važi samo za redove koji još postoje (posle izmene dana) */
    const izbor = vise ? izabrani.filter((z) => withKeys.some((d) => keyOf(d) === keyOf(z))) : [];

    return (
        <div onKeyDown={onKeyDown} className={vise ? 'grid-sa-alatima' : undefined} style={{ height: '100%' }}>
        {vise && (
            <div className="grid-alati">
                <span className="muted">{izbor.length ? `Izabrano: ${izbor.length}` : 'Označite redove za brisanje'}</span>
                <Button type="button" label="Obriši aktivnosti" icon="pi pi-trash" severity="danger" outlined size="small"
                    onClick={obrisiIzabrane} disabled={!izbor.length || !!edit}
                    tooltip="Izabrane aktivnosti se brišu, na njihovom mestu ostaje odmor" tooltipOptions={{ position: 'top', showOnDisabled: true }} />
            </div>
        )}
        {/* cellMemo={false}: ćelije zavise od stanja izmene (edit), a ne samo od podataka reda — sa memoizacijom se red ne otvara za izmenu */}
        <DataTable value={rows} dataKey="_key" size="small" cellMemo={false} emptyMessage={tableEmpty(emptyMessage, 'pi pi-clock')} scrollable scrollHeight={scrollHeight}
            rowClassName={(r) => (isEditing(r) || (tipRed && keyOf(r) === keyOf(tipRed)) ? 'row-selected' : '')} onRowDoubleClick={brziTip}
            {...(vise ? { selectionMode: 'checkbox', selection: izbor, onSelectionChange: (e) => setIzabrani(e.value), isDataSelectable: (e) => !edit && keyOf(e.data) !== '__new' } : {})}>
            {vise && <Column selectionMode="multiple" headerStyle={{ width: '2.75rem' }} />}
            {!readOnly && <Column header={addHeader} body={actionsBody} headerClassName="actions" bodyClassName="actions" style={{ width: '6.5rem' }} />}
            <Column header="Tip" body={typeBody} style={{ minWidth: '9rem' }} />
            <Column header="Vreme početka" body={timeBody('startTime')} style={{ minWidth: startWidth || '9rem' }} />
            <Column header="Vreme završetka" body={timeBody('endTime')} style={{ minWidth: startWidth || '9rem' }} />
            <Column header="Vreme trajanja" body={totalBody} headerClassName="num" bodyClassName="num" style={{ minWidth: '7rem' }} />
            {/* dodatne kolone samo za prikaz (npr. izvor reda u bulk unosu listića) */}
            {extraColumns.map((c) => <Column key={c.header} header={c.header} body={c.body} style={c.style} />)}
        </DataTable>
        <OverlayPanel ref={tipPanel} onHide={() => setTipRed(null)} className="tip-panel">
            <ListBox value={tipRed?.activityType} options={ACTIVITY_TYPES} onChange={(e) => upisiTip(e.value)} aria-label="Tip aktivnosti"
                itemTemplate={simboli ? (o) => <AktivnostSimbol tip={o.value} label={o.label} /> : undefined} />
        </OverlayPanel>
        </div>
    );
}

